import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { buildSubStateSelector } from '../../store/utilities/useAppStateSelector';

export interface FeatureFlag {
    id: string,
    name: string,
    enabled: boolean
};

const initialState = {
    featureFlags: [] as FeatureFlag[],
    loadingFeatureFlags: false,
    // False until a /featureflags request settles. Consumers must not read an
    // absent flag as "disabled" before this flips, or they act on an empty list.
    featureFlagsLoaded: false,
    // Which user the loaded flags belong to (null = anonymous). The endpoint is
    // identity-scoped — it left-joins FeatureFlagOverrides on UserId — so flags
    // fetched anonymously are not an answer for a signed-in user. App.tsx fires
    // the fetch once before auth resolves and again after, so without this the
    // settled signal can describe the wrong identity.
    featureFlagsUserId: null as string | null,
    // True when the last settled attempt failed. Distinct from "settled with the
    // flag absent": we do not know the flag's value, so consumers must not treat
    // it as disabled and act on that.
    featureFlagsError: false,
};

export type FeatureFlagAppState = typeof initialState;

export const slice = createSlice<FeatureFlagAppState, {
    setFeatureFlags : CaseReducer<FeatureFlagAppState, PayloadAction<FeatureFlag[]>>,
    setLoadingFeatureFlags : CaseReducer<FeatureFlagAppState, PayloadAction<boolean>>,
    setFeatureFlagsSettled : CaseReducer<FeatureFlagAppState, PayloadAction<{ userId: string | null, error: boolean }>>,
}>({
    name: "auth",
    initialState,
    reducers: {
        setFeatureFlags: (state, action) => {
            state.featureFlags = action.payload;
        },
        setLoadingFeatureFlags: (state, action) => {
            state.loadingFeatureFlags = action.payload;
        },
        // userId is who the settled flags describe (null = anonymous).
        setFeatureFlagsSettled: (state, action) => {
            state.featureFlagsLoaded = true;
            state.featureFlagsUserId = action.payload.userId;
            state.featureFlagsError = action.payload.error;
        },
    }
});

export const useAppStateFeatureFlagSelector = buildSubStateSelector<FeatureFlagAppState>(state => state.featureFlag);
export const {
    setFeatureFlags,
    setLoadingFeatureFlags,
    setFeatureFlagsSettled,
} = slice.actions;
export const featureFlagReducer = slice.reducer;
